import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardHeader, CardBody, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { ShoppingCart, Search, DollarSign, Package, Clock, Ban, Trash2 } from 'lucide-react';
import { api } from '../../utils/api';
import toast from 'react-hot-toast';
import { OrderCancelModal } from '../../components/OrderCancelModal';

export function OrdersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedOrderForCancel, setSelectedOrderForCancel] = useState(null);
  const [highlightedOrderId, setHighlightedOrderId] = useState(null);
  const highlightedRef = useRef(null);

  useEffect(() => {
    loadOrders();
  }, []);

  // Check for highlighted order from URL params
  useEffect(() => {
    const orderId = searchParams.get('orderId');
    if (orderId && orders.length > 0) {
      setHighlightedOrderId(parseInt(orderId));

      // Scroll to highlighted order after a short delay
      setTimeout(() => {
        if (highlightedRef.current) {
          highlightedRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);

      // Clear highlight after 3 seconds
      setTimeout(() => {
        setHighlightedOrderId(null);
        setSearchParams({});
      }, 3000);
    }
  }, [orders, searchParams, setSearchParams]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/orders');

      if (response.success) {
        setOrders(response.orders);
      } else {
        toast.error('Fehler beim Laden der Bestellungen');
      }
    } catch (error) {
      console.error('Error loading orders:', error);
      toast.error('Fehler beim Laden der Bestellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrder = async (orderId, orderName) => {
    if (!window.confirm(`Möchten Sie die stornierte Bestellung ${orderName} wirklich löschen?\n\nDies entfernt sie aus der Ansicht.`)) {
      return;
    }

    try {
      await api.delete(`/admin/orders/${orderId}`);
      toast.success('Bestellung erfolgreich gelöscht');
      loadOrders();
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error('Fehler beim Löschen der Bestellung');
    }
  };

  const formatDate = (dateString) => {
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'warning',
      confirmed: 'success',
      fulfilled: 'success',
      cancelled: 'error',
    };
    const labels = {
      pending: 'Ausstehend',
      confirmed: 'Bestätigt',
      fulfilled: 'Erfüllt',
      cancelled: 'Storniert',
    };
    return <Badge variant={variants[status] || 'default'}>{labels[status] || status}</Badge>;
  };

  const getFinancialStatusBadge = (status) => {
    const variants = {
      paid: 'success',
      pending: 'warning',
      refunded: 'error',
      partially_refunded: 'warning',
    };
    const labels = {
      paid: 'Bezahlt',
      pending: 'Ausstehend',
      refunded: 'Erstattet',
      partially_refunded: 'Teilweise erstattet',
    };
    return <Badge variant={variants[status] || 'default'}>{labels[status] || status}</Badge>;
  };

  const filteredOrders = orders.filter((order) => {
    const query = searchQuery.toLowerCase();
    const matchesSearch =
      order.order_id?.toLowerCase().includes(query) ||
      order.first_name?.toLowerCase().includes(query) ||
      order.last_name?.toLowerCase().includes(query) ||
      order.email?.toLowerCase().includes(query);

    const matchesFilter =
      filterStatus === 'all' ||
      order.status === filterStatus ||
      order.financial_status === filterStatus;

    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-gray-400 mt-4">Lade Bestellungen...</p>
        </div>
      </div>
    );
  }

  const stats = {
    total: orders.length,
    pending: orders.filter((o) => o.status === 'pending').length,
    confirmed: orders.filter((o) => o.status === 'confirmed').length,
    revenue: orders
      .filter((o) => o.financial_status === 'paid')
      .reduce((sum, o) => sum + (o.total_amount || 0), 0),
  };

  return (
    <div>
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Bestellungen</h1>
            <p className="text-gray-400 mt-1">Verwalte alle Bestellungen und deren Status</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardBody className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-blue-100 p-3 rounded-lg">
                <ShoppingCart className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Gesamt</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-yellow-100 p-3 rounded-lg">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Ausstehend</p>
                <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-green-100 p-3 rounded-lg">
                <Package className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Bestätigt</p>
                <p className="text-2xl font-bold text-gray-900">{stats.confirmed}</p>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-orange-100 p-3 rounded-lg">
                <DollarSign className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Umsatz</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.revenue)}</p>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <CardTitle>Alle Bestellungen</CardTitle>
            <div className="flex items-center gap-4">
              <div className="flex gap-2">
                <Button
                  variant={filterStatus === 'all' ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setFilterStatus('all')}
                >
                  Alle
                </Button>
                <Button
                  variant={filterStatus === 'pending' ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setFilterStatus('pending')}
                >
                  Ausstehend
                </Button>
                <Button
                  variant={filterStatus === 'paid' ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setFilterStatus('paid')}
                >
                  Bezahlt
                </Button>
                <Button
                  variant={filterStatus === 'cancelled' ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setFilterStatus('cancelled')}
                >
                  Storniert
                </Button>
              </div>
              <div className="relative">
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Suchen..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent w-64"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {filteredOrders.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-400">
                {searchQuery || filterStatus !== 'all'
                  ? 'Keine Bestellungen gefunden'
                  : 'Noch keine Bestellungen vorhanden'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Bestellung</TableHead>
                  <TableHead>Kunde</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Zahlung</TableHead>
                  <TableHead>Betrag</TableHead>
                  <TableHead>Datum</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map((order) => {
                  const isHighlighted = highlightedOrderId === order.id;
                  return (
                  <TableRow
                    key={order.id}
                    hover
                    ref={isHighlighted ? highlightedRef : null}
                    className={isHighlighted ? 'bg-yellow-50 animate-pulse' : ''}
                  >
                    <TableCell>
                      <div>
                        <p className="font-medium text-gray-900">{order.order_id}</p>
                        {order.shopify_order_number && (
                          <p className="text-sm text-gray-400">Shopify: #{order.shopify_order_number}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-gray-900">
                          {order.first_name} {order.last_name}
                        </p>
                        <p className="text-sm text-gray-400">{order.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(order.status)}</TableCell>
                    <TableCell>
                      {order.financial_status ? (
                        getFinancialStatusBadge(order.financial_status)
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="font-medium text-gray-900">
                        {formatCurrency(order.total_amount || 0)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-600">{formatDate(order.created_at)}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (!order.shopify_order_id) {
                              toast.error('Keine Shopify Order ID gefunden');
                              return;
                            }
                            navigate(`/bookings-calendar?orderId=${encodeURIComponent(order.shopify_order_id)}`);
                          }}
                        >
                          Details
                        </Button>

                        {/* Show Cancel button for paid/active orders (not yet cancelled or refunded) */}
                        {!(order.status === 'cancelled' || order.financial_status === 'refunded') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedOrderForCancel(order);
                            }}
                            className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                            title="Bestellung stornieren"
                          >
                            <Ban className="w-4 h-4" />
                          </Button>
                        )}

                        {/* Show Delete button ONLY for cancelled/refunded orders */}
                        {(order.status === 'cancelled' || order.financial_status === 'refunded') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteOrder(order.id, order.order_id);
                            }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Bestellung löschen"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Order Cancel Modal */}
      {selectedOrderForCancel && (
        <OrderCancelModal
          order={selectedOrderForCancel}
          onClose={() => setSelectedOrderForCancel(null)}
          onSuccess={() => {
            setSelectedOrderForCancel(null);
            loadOrders();
          }}
        />
      )}
    </div>
  );
}
